import React, { isValidElement, ReactElement, ReactNode, useMemo } from 'react';

import { SelectOptionType } from '../types';
import { flattenOptions } from '../util';

function toArray<T>(value: T | T[]): T[] {
  if (Array.isArray(value)) {
    return value;
  }
  return value !== undefined ? [value] : [];
}

function fillOptionsWithMissingValue<T extends SelectOptionType>(options: T[]): T[] {
  return options.map((item) => ({
    ...item,
    label: item.label === undefined ? item.value : item.label,
  }));
}

function convertNodeToOption(node: ReactElement, index: React.Key) {
  const {
    key,
    props: { value, children, ...restProps },
  } = node as ReactElement;

  return {
    key: key !== null ? key : index,
    value: value !== undefined ? value : key !== null ? key : index,
    label: children !== undefined ? children : value,
    ...restProps,
  };
}

function convertChildrenToData<T extends SelectOptionType>(nodes: ReactNode, groupKey?: React.Key) {
  while (isValidElement(nodes) && typeof nodes.type === 'symbol') {
    nodes = nodes.props.children;
  }

  return toArray(nodes)
    .map((node, index): T | null => {
      if (!isValidElement(node) || !node.type) return null;

      const {
        type: { isSelectOption, isSelectOptGroup },
        key,
        props: { children, ...restProps },
      } = node as React.ReactElement & {
        type: Record<'isSelectOption' | 'isSelectOptGroup', boolean>;
      };

      if (isSelectOption) {
        return convertNodeToOption(node, groupKey ? `${groupKey}${index}__` : index);
      } else if (isSelectOptGroup) {
        groupKey = `__COBALT_SELECT_GROUP__${key === null ? index : key}__`;
        return {
          key: groupKey,
          label: key,
          ...restProps,
          options: convertChildrenToData(children, groupKey),
        };
      } else {
        return null;
      }
    })
    .filter((data) => data) as T[];
}

export default function useOptions<T extends SelectOptionType>(options: T[] | undefined, children: React.ReactNode) {
  return useMemo(() => {
    let newOptions = options;

    if (newOptions !== undefined) {
      newOptions = fillOptionsWithMissingValue(newOptions);
    } else {
      newOptions = convertChildrenToData(children);
    }

    return flattenOptions(newOptions);
  }, [children, options]);
}
